import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useScrollReveal from "../hooks/useScrollReveal";
import { caseStudies } from "../mock/mock";

const NotFound = () => (
  <div className="min-h-screen flex flex-col">
    <Header forceSolid />
    <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pt-24">
      <p className="display text-7xl font-extrabold uppercase">404</p>
      <p className="mono uppercase tracking-widest text-neutral-500 mt-4">Case study not found</p>
      <Link to="/" className="btn-ink mt-8">Back Home</Link>
    </div>
    <Footer />
  </div>
);

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cs = caseStudies.find((c) => c.slug === slug);
  useScrollReveal([slug]);

  if (!cs) return <NotFound />;

  const others = caseStudies.filter((c) => c.slug !== slug).slice(0, 3);

  return (
    <div className="bg-[#F4F1EA]">
      <Header forceSolid />

      {/* Hero */}
      <section className="bg-[#0A0A0A] text-[#F4F1EA] pt-36 pb-20 relative overflow-hidden">
        <div className="container-x relative">
          <div className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-[#F4F1EA]/50 mb-8">
            <button onClick={() => navigate("/")} className="hover:text-[#F4F1EA]">Home</button>
            <span>/</span>
            <button onClick={() => navigate("/#case-studies")} className="hover:text-[#F4F1EA]">Case Studies</button>
            <span>/</span>
            <span className="text-[#F4F1EA]">{cs.tag}</span>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="mono text-[11px] uppercase tracking-widest bg-[#F4F1EA] text-[#0A0A0A] px-3 py-1.5 inline-block mb-6">{cs.tag}</span>
              <h1 className="display font-extrabold uppercase leading-[0.92] tracking-tight text-[clamp(2rem,3.6vw,3.3rem)]">{cs.title}</h1>
              <p className="mt-6 text-[#F4F1EA]/70 text-lg max-w-xl leading-relaxed">{cs.desc}</p>
              <button onClick={() => navigate("/#contact")} className="btn-paper mt-9 group">
                Start a Similar Project <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            <div className="lg:col-span-5">
              <div className="border-2 border-[#F4F1EA] shadow-[10px_10px_0_#F4F1EA]">
                <img src={cs.image} alt={cs.title} className="w-full h-[360px] object-cover bw" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results band */}
      <section className="bg-white border-b-2 border-[#0A0A0A]">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 border-x-2 border-[#0A0A0A]">
          {cs.results.map((r, i) => (
            <div key={r.label} className={`p-7 border-[#0A0A0A] ${i !== 3 ? "md:border-r-2" : ""} border-b-2 md:border-b-0 ${i % 2 === 0 ? "border-r-2 md:border-r-0" : ""} ${i < 2 ? "border-b-2 md:border-b-0" : ""}`}>
              <p className="display text-4xl md:text-5xl font-extrabold"><span className="hl">{r.value}</span></p>
              <p className="mono text-[11px] uppercase tracking-wide text-neutral-500 mt-3">{r.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Challenge / Solution */}
      <section className="section-pad grid-dots border-b-2 border-[#0A0A0A]">
        <div className="container-x grid md:grid-cols-2 gap-8">
          <div className="border-2 border-[#0A0A0A] bg-white p-8">
            <p className="eyebrow text-neutral-500">/ The Challenge</p>
            <p className="mt-5 text-neutral-700 leading-relaxed text-lg">{cs.challenge}</p>
          </div>
          <div className="border-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#F4F1EA] p-8">
            <p className="eyebrow text-[#F4F1EA]/50">/ Our Solution</p>
            <p className="mt-5 text-[#F4F1EA]/85 leading-relaxed text-lg">{cs.solution}</p>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="section-pad bg-white border-b-2 border-[#0A0A0A]">
        <div className="container-x">
          <p className="eyebrow text-neutral-500">/ Our Approach</p>
          <h2 className="mt-4 display text-3xl md:text-4xl font-extrabold uppercase leading-[0.95] mb-12">How We <span className="hl">Delivered</span></h2>
          <div className="grid md:grid-cols-4 border-2 border-[#0A0A0A]">
            {cs.approach.map((a, i) => (
              <div key={a} className={`p-7 border-[#0A0A0A] ${i !== 3 ? "md:border-r-2" : ""} border-b-2 md:border-b-0`}>
                <span className="display text-4xl font-extrabold stroke-text">{String(i + 1).padStart(2, "0")}</span>
                <p className="display text-base font-bold uppercase mt-4">{a}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-10">
            {cs.tech.map((t) => (
              <span key={t} className="mono text-[12px] font-bold uppercase tracking-widest px-4 py-2.5 border-2 border-[#0A0A0A]">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="section-pad bg-[#0A0A0A] text-[#F4F1EA]">
        <div className="container-x max-w-4xl">
          <span className="display text-7xl font-extrabold leading-none">“</span>
          <p className="display text-2xl md:text-3xl font-bold uppercase leading-[1.15] mt-2">{cs.quote.text}</p>
          <p className="mono text-[12px] uppercase tracking-widest text-[#F4F1EA]/60 mt-6">— {cs.quote.author}</p>
        </div>
      </section>

      {/* Other case studies */}
      <section className="section-pad grid-dots border-b-2 border-[#0A0A0A]">
        <div className="container-x">
          <p className="eyebrow text-neutral-500">/ More Work</p>
          <h2 className="mt-4 display text-3xl md:text-4xl font-extrabold uppercase leading-[0.95] mb-12">Other <span className="hl">Case Studies</span></h2>
          <div className="grid md:grid-cols-3 gap-6">
            {others.map((o) => (
              <button key={o.slug} onClick={() => navigate(`/case-studies/${o.slug}`)} className="card-brutal overflow-hidden text-left group" data-cursor>
                <div className="relative h-40 overflow-hidden border-b-2 border-[#0A0A0A]">
                  <img src={o.image} alt={o.title} className="w-full h-full object-cover bw" />
                  <span className="absolute top-0 left-0 mono text-[11px] uppercase tracking-widest bg-[#0A0A0A] text-[#F4F1EA] px-3 py-1.5">{o.tag}</span>
                </div>
                <div className="p-5">
                  <h3 className="display text-base font-bold uppercase leading-tight mb-3">{o.title}</h3>
                  <span className="mono text-[11px] font-bold uppercase tracking-widest inline-flex items-center gap-2">View <ArrowUpRight size={13} /></span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-[#F4F1EA] py-20">
        <div className="container-x text-center">
          <h2 className="display text-3xl md:text-5xl font-extrabold uppercase leading-[0.95]">
            Your Story <span className="hl-invert">Next</span>?
          </h2>
          <button onClick={() => navigate("/#contact")} className="btn-paper mt-8 mx-auto group">
            Talk to Our Experts <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudyDetail;
